import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Avatar,
  Divider,
  CircularProgress,
  IconButton,
  Paper,
  FormControl,
  Select,
  Tooltip,
  MenuItem,
  TextField,
} from "@mui/material";

import { BackButton } from "./functions";
import { AnimatedStep } from "./Animations";
import LoadingBackdrop from "./LoadingBackdrop";
import ShareLink from "./ShareLink";
import MoreToCome from "./MoreToCome";
import ShareIcon from "@mui/icons-material/Share";
import LoadingDialog from "./LoadingDialog";
import WelcomeDialogLessons from "./WelcomeDialogLessons";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import ImageIcon from "@mui/icons-material/Image";
import { useSearchParams, useParams } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { callApi } from "./api";
import { useTheme } from "@mui/material/styles";
import { getStyles } from "../index";
import { generateAndProcessImage, trackLesson } from "./functions";
import { OrbitProgress } from "react-loading-indicators";
import { useSnackbar } from "./SnackbarHandler";
import LanguageSelector from "./LanguageSelector";
import PrintIcon from "@mui/icons-material/Print";
import { handlePrintLetterBox } from "./printFunctions"; // Adjust the import path as needed

import { ThemeSelector } from "./ThemeSelector";
import ImageGenerationModal from "./ImageGenerationModal";
import GradeSelector, { gradeInstructions } from "./GradeSelector";
import EditIcon from "@mui/icons-material/Edit";

import Logger from "./Logger";
import LessonDrawer from "./LessonDrawer";
import SaveLessonDialog from "./SaveLessonDialog";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import HistoryIcon from "@mui/icons-material/History";
import HoverableImage from "./HoverableImage";

import { ThemeSelectorFlow } from "./ThemeSelectorFlow";

import {
  Science as ScienceIcon,
  Castle as CastleIcon,
  Psychology as PsychologyIcon,
  Pets as PetsIcon,
  Public as PublicIcon,
  EmojiEvents as EmojiEventsIcon,
  Museum as MuseumIcon,
  Nature as NatureIcon,
} from "@mui/icons-material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { db } from "./firebase";
import {
  collection,
  updateDoc,
  query,
  where,
  limit,
  serverTimestamp,
  addDoc,
  getDocs,
} from "firebase/firestore";

import ShareDialog from "./ShareDialog";

import InfoBox from "./InfoBox";

const LetterBoxLesson = ({ user, setMinimized }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const [words, setWords] = useState([]);
  const [selectedLetters, setSelectedLetters] = useState({});
  const [isGeneratingLesson, setIsGeneratingLesson] = useState(false);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [generatedImages, setGeneratedImages] = useState(new Set());
  const [themeColor, setThemeColor] = useState("#8a59f4");
  const { showSnackbar } = useSnackbar();
  const [grade, setGrade] = useState("åk6");
  const theme = useTheme();
  const [isSharing, setIsSharing] = useState(false);
  const shareLinkButtonRef = useRef(null);
  const styles = getStyles(theme);
  const [language, setLanguage] = useState("svenska");
  // New configuration states
  const [themeType, setThemeType] = useState("generateWithAi");
  const [customTheme, setCustomTheme] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogOpenFriends, setShareDialogOpenFriends] = useState(false);
  const [wordCount, setWordCount] = useState(5);
  const [extraLettersCount, setExtraLettersCount] = useState(30);

  const [showImageModal, setShowImageModal] = useState(false);
  const [imageInstructions, setImageInstructions] = useState("");
  const [imageGenerationType, setImageGenerationType] = useState(null);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingSubtitle, setIsEditingSubtitle] = useState(false);
  const [titleText, setTitleText] = useState("Bokstavslådor");
  const [generatingImageIndex, setGeneratingImageIndex] = useState(null);
  const urlContentRef = useRef("");
  const [lessonTheme, setLessonTheme] = useState("generateWithAi");
  const [predefinedTheme, setPredefinedTheme] = useState(null);
  const lessonTitleRef = useRef(null); // Add this ref
  const transcriptRef = useRef("");
  const bookContentRef = useRef("");

  const uploadedFileRef = useRef("");

  const wikipediaContentRef = useRef("");
  const randomizedContentRef = useRef("");
  const coursePlanContentRef = useRef("");

  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get("v") === "t";
  const subject = searchParams.get("subject") || null;

  const toggleViewMode = () => {
    const currentParams = Object.fromEntries(searchParams.entries());
    if (currentParams.v === "t") {
      delete currentParams.v;
    } else {
      currentParams.v = "t";
    }
    setSearchParams(currentParams);
  };

  useEffect(() => {
    Logger.log("subject: ", subject);
  }, [subject]);

  useEffect(() => {
    Logger.log("lessonTheme: ", lessonTheme);
  }, [lessonTheme]);

  useEffect(() => {
    Logger.log("predefinedTheme: ", predefinedTheme);
  }, [predefinedTheme]);

  useEffect(() => {
    Logger.log("customTheme: ", customTheme);
  }, [customTheme]);

  useEffect(() => {
    Logger.log("youtubeTranscript: ", transcriptRef.current);
  }, [transcriptRef.current]);

  useEffect(() => {
    Logger.log("wikipediaContentRef: ", wikipediaContentRef.current);
  }, [wikipediaContentRef.current]);
  useEffect(() => {
    Logger.log("uploadedFileRef: ", uploadedFileRef.current);
  }, [uploadedFileRef.current]);

  useEffect(() => {
    Logger.log("randomizedContentRef: ", randomizedContentRef.current);
  }, [randomizedContentRef.current]);

  useEffect(() => {
    Logger.log("coursePlanContentRef: ", coursePlanContentRef.current);
  }, [coursePlanContentRef.current]);

  const pasteTextContentRef = useRef("");
  const analysisContentRef = useRef("");
  const resetThemeContent = () => {
    setLessonTheme("");
    setCustomTheme("");
    setPredefinedTheme(null);
    transcriptRef.current = "";
    uploadedFileRef.current = "";
    wikipediaContentRef.current = "";
    randomizedContentRef.current = "";
    bookContentRef.current = "";
    urlContentRef.current = "";
    analysisContentRef.current = "";
    coursePlanContentRef.current = "";
    pasteTextContentRef.current = "";
  };

  const [currentStep, setCurrentStep] = useState(1);
  useEffect(() => {
    if (words.length > 0) {
      setCurrentStep(3);
      setMinimized(true);
    } else {
      setMinimized(false);
    }
  }, [words, currentStep]);

  const { lessonId } = useParams();
  useEffect(() => {
    const loadLessonFromId = async () => {
      if (lessonId) {
        try {
          const q = query(
            collection(db, "lesson_usage"),
            where("__name__", ">=", lessonId),
            where("__name__", "<=", lessonId + "\uf8ff"),
            where("lessonType", "==", "LetterBoxLesson.js"),
            limit(1),
          );

          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const docData = querySnapshot.docs[0].data();

            if (!docData.hasOpened) {
              await updateDoc(querySnapshot.docs[0].ref, {
                hasOpened: true,
                openedAt: Date.now(),
              });
            }

            setWords(docData.data.words);
            setSelectedLetters(docData.data.selectedLetters);
            setTitleText(docData.lessonName);

            showSnackbar("Lektionen har laddats in.", "success");
          } else {
            showSnackbar("Kunde inte hitta lektionen", "error");
          }
        } catch (error) {
          console.error("Error loading lesson:", error);
          showSnackbar(
            "Ett fel uppstod när presentationen skulle laddas",
            "error",
          );
        }
      }
    };

    loadLessonFromId();
  }, lessonId);

  const handleSaveLessonClick = () => {
    setSaveDialogOpen(true);
  };

  const handleSaveWithComment = async (comment, name, coverImage, tags) => {
    const lessonData = {
      words: words,
      selectedLetters: selectedLetters,
      titleText: titleText,
    };

    await trackLesson(
      user.uid,
      "LetterBoxLesson.js",
      lessonData,
      null,
      true,
      comment,
      name,
      coverImage,
      tags,
    );
    setDrawerOpen(true);
    showSnackbar("Lektionen sparad i din lektionsbank!", "success");
  };

  const handleShare = async (
    lessonComment,
    lessonName,
    selectedImage,
    tags,
    shareData,
  ) => {
    setIsSharing(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    try {
      const lessonData = {
        words: words,
        selectedLetters: selectedLetters,
        titleText: titleText,
      };

      // Create a document for each student and then create notifications
      const sharePromises = shareData.studentIds.map(async (studentId) => {
        const documentData = {
          userId: user.uid,
          shared: true,
          sharedTo: studentId,
          comment: shareData.comment,
          sharedAt: Date.now(),
          fileUrl: shareData.fileUrl,
          lessonType: "LetterBoxLesson.js",
          timestamp: serverTimestamp(),
          lessonComment,
          lessonName,
          coverImage: selectedImage,
          hasPrinted: true,
          data: lessonData,
        };
        // Create lesson document and get ID
        const lessonDoc = await addDoc(
          collection(db, "lesson_usage"),
          documentData,
        );
        Logger.log(
          "Adding document for student",
          studentId,
          ":",
          JSON.stringify(documentData, null, 2),
        );
        // Create notification with lesson doc ID
        await addDoc(collection(db, "notifications"), {
          type: "lesson_shared",
          recipientId: studentId,
          senderId: user.uid,
          read: false,
          createdAt: serverTimestamp(),
          lessonData: {
            lessonId: lessonDoc.id,
            lessonType: documentData.lessonType,
            lessonName: documentData.lessonName,
            fileUrl: documentData.fileUrl,
            comment: documentData.comment,
            coverImage: selectedImage,
          },
        });
        return lessonDoc;
      });
      await Promise.all(sharePromises);
      showSnackbar(`Du har nu delat lektionen!`, "success");
    } catch (error) {
      showSnackbar("Fel vid delande av lektionen: " + error.message, "error");
      console.error(error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleImageButtonClick = (type, wordIndex = null) => {
    setImageGenerationType(type);
    setGeneratingImageIndex(wordIndex); // Store the wordIndex for single image generation
    setShowImageModal(true);
  };

  const handleCloseModal = () => {
    setShowImageModal(false);
    setImageInstructions("");
  };

  const handleGenerateWithInstructions = async () => {
    handleCloseModal();

    if (generatingImageIndex !== null) {
      // Handle single image generation
      await handleSingleImageGeneration(generatingImageIndex);
    } else {
      // Handle bulk image generation
      await handleGenerateImages();
    }
  };

  const generateLetterPool = (word) => {
    const wordLetters = word.split("");
    const alphabet = "abcdefghijklmnopqrstuvwxyzåäö".split("");
    const additionalLetters = Array.from(
      { length: extraLettersCount - wordLetters.length },
      () => alphabet[Math.floor(Math.random() * alphabet.length)],
    );
    return [...wordLetters, ...additionalLetters].sort(
      () => Math.random() - 0.5,
    );
  };

  const handleGenerateLesson = async () => {
    setIsGeneratingLesson(true);
    try {
      let themeToUse;

      if (lessonTheme === "youtube") {
        themeToUse = transcriptRef.current;
      } else if (lessonTheme === "customTheme") {
        themeToUse = customTheme;
      } else if (lessonTheme === "predefined") {
        themeToUse = predefinedTheme;
      } else if (lessonTheme === "fileUpload") {
        themeToUse = uploadedFileRef.current;
      } else if (lessonTheme === "wikipedia") {
        themeToUse = wikipediaContentRef.current;
      } else if (lessonTheme === "randomizer") {
        themeToUse = randomizedContentRef.current;
      } else if (lessonTheme === "book") {
        themeToUse = bookContentRef.current;
      } else if (lessonTheme === "url") {
        themeToUse = urlContentRef.current;
      } else if (lessonTheme === "vision") {
        themeToUse = analysisContentRef.current;
      } else if (lessonTheme === "pasteText") {
        themeToUse = pasteTextContentRef.current;
      } else {
        //
        themeToUse = coursePlanContentRef.current;
      }

      Logger.log("themeToUse: ", themeToUse);

      const gradeInstructionsText = gradeInstructions[grade];

      const response = await callApi(
        [
          {
            role: "system",
            content:
              "Du är en assistent som genererar enkla ord för bokstavsövningar.",
          },
          {
            role: "user",
            content: `Generera JSON med exakt ${wordCount}   ord relaterade till temat "${themeToUse}". Orden ska vara på ${language}. Varje ord ska vara 3-8 bokstäver långt.
            ${gradeInstructionsText}

             Säkerställ att allt som genereras är enbart en JSON array. Exempel på format:
            {
              "words": [
                { "word": "katt" },
                { "word": "bil" },
                { "word": "hund" },
                { "word": "boll" },
                { "word": "fisk" }
              ]
            }`,
          },
        ],
        4000,
        true,
        user.uid,
      );

      let messageContent = response.data.choices[0].message.content.trim();

      if (messageContent.startsWith("```json")) {
        messageContent = messageContent.slice(7, -3).trim();
      } else if (messageContent.startsWith("```")) {
        messageContent = messageContent.slice(3, -3).trim();
      }

      const data = JSON.parse(messageContent);

      const wordsWithLetters = data.words.map((wordObj) => ({
        ...wordObj,
        letters: generateLetterPool(wordObj.word),
        image: null,
      }));

      setWords(wordsWithLetters);
      setSelectedLetters({});
      showSnackbar("Nya ord har genererats", "success");

      await trackLesson(user.uid, "LetterBoxLesson.js", null, {
        wordCount: wordCount,
        language: language,
        lessonTheme: lessonTheme,
      });
    } catch (error) {
      console.error("Error generating lesson:", error);
      showSnackbar("Kunde inte generera nya ord", "error");
    } finally {
      setIsGeneratingLesson(false);
    }
  };

  const handleImageUpdate = (wordIndex, newImageUrl) => {
    const updatedWords = [...words];
    updatedWords[wordIndex] = {
      ...updatedWords[wordIndex],
      image: newImageUrl,
    };
    setWords(updatedWords);
    showSnackbar("Bild har uppdaterats", "success");
  };

  const handleSingleImageGeneration = async (wordIndex) => {
    try {
      const word = words[wordIndex];
      const prompt = `Create a simple, clear image representing the word "${word.word}". No text in the image. ${imageInstructions}`;

      const imageUrl = await generateAndProcessImage(prompt, user.uid, {
        size: "1024x1024",
        model: "dall-e-3",
        quality: "standard",
      });

      handleImageUpdate(wordIndex, imageUrl);
      showSnackbar("Bild har genererats", "success");
    } catch (error) {
      console.error("Error generating image:", error);
      showSnackbar("Kunde inte generera bild", "error");
    } finally {
      setGeneratingImageIndex(null);
    }
  };
  const handleGenerateImages = async () => {
    setIsGeneratingImages(true);
    setGeneratedImages(new Set());

    try {
      // Create an array of promises for all image generation tasks
      const imagePromises = words.map(async (word, index) => {
        try {
          const prompt = `Create a simple, clear image representing the word "${word.word}". No text in the image. ${imageInstructions}`;

          const imageUrl = await generateAndProcessImage(prompt, user.uid, {
            size: "1024x1024",
            model: "dall-e-3",
            quality: "standard",
          });

          // Return the updated word object and its index
          return { index, updatedWord: { ...word, image: imageUrl } };
        } catch (error) {
          console.error(
            `Error generating image for word "${word.word}":`,
            error,
          );
          showSnackbar(`Kunde inte generera bild för "${word.word}"`, "error");
          // Return the original word and its index if there's an error
          return { index, updatedWord: word };
        }
      });

      // Wait for all promises to resolve
      const results = await Promise.all(imagePromises);

      // Update the words array with all results
      const updatedWords = [...words];
      const successfulIndices = new Set();

      results.forEach(({ index, updatedWord }) => {
        updatedWords[index] = updatedWord;
        // Only add to generated images if the image was actually generated
        if (updatedWord.image && updatedWord.image !== words[index].image) {
          successfulIndices.add(index);
        }
      });

      setWords(updatedWords);
      setGeneratedImages(successfulIndices);

      showSnackbar("Alla bilder har genererats!", "success");
    } catch (error) {
      console.error("Error in image generation process:", error);
      showSnackbar("Ett fel uppstod när bilderna skulle genereras", "error");
    } finally {
      setIsGeneratingImages(false);
    }
  };
  const handleLetterClick = (wordIndex, letter, letterIndex) => {
    setSelectedLetters((prev) => {
      const currentSelected = prev[wordIndex] || [];
      if (!currentSelected.find((item) => item.index === letterIndex)) {
        return {
          ...prev,
          [wordIndex]: [...currentSelected, { letter, index: letterIndex }],
        };
      }
      return prev;
    });
  };

  const handleClearWord = (wordIndex) => {
    setSelectedLetters((prev) => ({
      ...prev,
      [wordIndex]: [],
    }));
  };

  const isWordComplete = (wordIndex) => {
    const selected = selectedLetters[wordIndex] || [];
    const targetWord = words[wordIndex]?.word || "";
    const selectedWord = selected.map((s) => s.letter).join("");
    return selectedWord === targetWord;
  };

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
          margin: "0 auto",
        }}
      >
        {!viewMode && (
          <>
            {currentStep !== 3 && (
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  ...styles.newH1,
                }}
              >
                Bokstavslådor
              </Typography>
            )}
          </>
        )}
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          {!viewMode && (
            <>
              <Tooltip title="Dina sparade lektioner">
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  sx={{ ...styles.iconButtonBg }}
                >
                  <BookmarkBorderIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        <LessonDrawer
          userId={user.uid}
          lessonType="LetterBoxLesson.js"
          theme={theme}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          lessonIcon={PrintIcon}
          onSelectLesson={(lesson) => {
            setWords(lesson.data.words);
            setSelectedLetters(lesson.data.selectedLetters);
            setTitleText(lesson.lessonName);

            setDrawerOpen(false);
            showSnackbar(`Lektionen har laddats in.`, "success");
            setTimeout(() => {
              lessonTitleRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start", // This ensures it scrolls to the top of the element
              });
            }, 100);
          }}
        />
        {!viewMode && (
          <>
            {currentStep !== 3 && (
              <InfoBox
                title="Powerpoint Instructions"
                instructions={[
                  "Generera ord som eleven ska hitta bland mängder av bokstäver",
                  "Lägg till AI-genererade bilder för varje ord",
                  "Skriv ut lektionen",
                ]}
                theme={theme}
                styles={styles}
                lessonType="LetterBoxLesson.js"
                user={user}
              />
            )}
            {currentStep === 1 && (
              <ThemeSelectorFlow
                subject={subject}
                value={lessonTheme}
                onChange={(e) => setLessonTheme(e.target.value)}
                customTheme={customTheme}
                onCustomThemeChange={(e) => {
                  Logger.log("onCustomThemeChange", e.target.value);
                  setCustomTheme(e.target.value);
                  setCurrentStep(2);
                }}
                onPastedTextChange={(content) => {
                  Logger.log("onPastedTextChange", content.target.value);
                  pasteTextContentRef.current = content.target.value;
                  setCurrentStep(2);
                }}
                onAnalysisContentChange={(content) => {
                  Logger.log("onAnalysisContentChange", content.analysis);
                  analysisContentRef.current = content.analysis;
                  setCurrentStep(2);
                }}
                onBookContentChange={(content) => {
                  Logger.log("onBookContentChange", content.substring(0, 100));
                  bookContentRef.current = content;
                  setCurrentStep(2);
                }}
                onUrlContentChange={(content) => {
                  Logger.log(
                    "Parent: URL content received",
                    content.substring(0, 300),
                  );
                  urlContentRef.current = content;
                  setCurrentStep(2);
                }}
                onPredefinedThemeChange={(e) =>
                  setPredefinedTheme(e.target.value)
                }
                onYoutubeTranscriptChange={(transcriptContent) => {
                  Logger.log(
                    "onYoutubeTranscriptChange körs med: ",
                    transcriptContent,
                  );
                  transcriptRef.current = transcriptContent;
                  setCurrentStep(2);
                }}
                onFileUpload={(content) => {
                  Logger.log("onFileUpload körs med: ", content);
                  uploadedFileRef.current = content;
                  setCurrentStep(2);
                }}
                onWikipediaContentChange={(content) => {
                  Logger.log(
                    "Parent: Wikipedia content received",
                    content.substring(0, 100),
                  );
                  wikipediaContentRef.current = content;
                  setCurrentStep(2);
                }}
                onRandomizedContentChange={(content) => {
                  Logger.log("randomizedContentRef", content.substring(0, 100));
                  randomizedContentRef.current = content;
                  setCurrentStep(2);
                }}
                onCoursePlanContentChange={(content) => {
                  Logger.log("onCoursePlanChange", content.substring(0, 100));
                  coursePlanContentRef.current = content;
                  setCurrentStep(2);
                }}
                lessonType="LetterBoxLesson.js"
                user={user}
                theme={theme}
                styles={styles}
                onLessonSelect={(lesson) => {
                  Logger.log("onLessonSelect körs");
                  setWords(lesson.data.words);
                  setSelectedLetters(lesson.data.selectedLetters);
                  setTitleText(lesson.lessonName);
                  setTimeout(() => {
                    lessonTitleRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start", // This ensures it scrolls to the top of the element
                    });
                  }, 100);
                  showSnackbar("Lektionen har laddats in.", "success");
                }}
              />
            )}
          </>
        )}
        {currentStep === 2 && (
          <>
            <BackButton
              currentStep={currentStep}
              onClick={() => {
                setCurrentStep(currentStep - 1);

                resetThemeContent();
              }}
              show={currentStep !== 1}
            />
            <AnimatedStep isRevealed={true}>
              <Box sx={{ ...styles.headlineLessonBox, mt: 6 }}>
                <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                  <span>2</span> Generera lektionen
                </Typography>
              </Box>

              <Box sx={{ ...styles.freshBox, padding: "30px" }}>
                {/* Configuration Section */}
                <Box sx={{ mb: 4 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={12}>
                      <Typography
                        variant="formLabelSinUnderline"
                        sx={{ fontWeight: "500", mb: 0 }}
                      >
                        Årskurs:
                      </Typography>
                      <Box sx={{ mt: 1, mb: 3 }}>
                        <GradeSelector grade={grade} setGrade={setGrade} />
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="formLabelSinUnderline"
                        sx={{ fontWeight: "500", mb: 0 }}
                      >
                        Antal ord:
                      </Typography>
                      <FormControl
                        fullWidth
                        margin="normal"
                        sx={{ mb: 3, mt: 1 }}
                      >
                        <Select
                          value={wordCount}
                          onChange={(e) => setWordCount(e.target.value)}
                        >
                          <MenuItem value={3}>3 ord</MenuItem>
                          <MenuItem value={4}>4 ord</MenuItem>
                          <MenuItem value={5}>5 ord</MenuItem>
                          <MenuItem value={6}>6 ord</MenuItem>
                          <MenuItem value={8}>8 ord</MenuItem>
                          <MenuItem value={10}>10 ord</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="formLabelSinUnderline"
                        sx={{ fontWeight: "500", mb: 0 }}
                      >
                        Antal extra bokstäver:
                      </Typography>
                      <FormControl
                        fullWidth
                        margin="normal"
                        sx={{ mb: 3, mt: 1 }}
                      >
                        <Select
                          value={extraLettersCount}
                          onChange={(e) => setExtraLettersCount(e.target.value)}
                        >
                          <MenuItem value={10}>10 bokstäver</MenuItem>
                          <MenuItem value={15}>15 bokstäver</MenuItem>
                          <MenuItem value={20}>20 bokstäver</MenuItem>
                          <MenuItem value={25}>25 bokstäver</MenuItem>
                          <MenuItem value={30}>30 bokstäver</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "500" }}
                      >
                        Språk:
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <LanguageSelector
                          language={language}
                          setLanguage={setLanguage}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Button
                  variant={
                    theme.palette.mode === "light" ? "purp" : "contained"
                  }
                  color="primary"
                  onClick={handleGenerateLesson}
                  disabled={isGeneratingLesson}
                  startIcon={
                    !isGeneratingLesson ? (
                      <GenerateIcon />
                    ) : (
                      <CircularProgress size={20} />
                    )
                  }
                  sx={{ mr: 2, mt: 4 }}
                >
                  {isGeneratingLesson ? "Genererar..." : "Generera lektion"}
                </Button>
              </Box>
            </AnimatedStep>
          </>
        )}

        {currentStep === 3 && (
          <>
            <BackButton
              currentStep={currentStep}
              onClick={() => {
                setCurrentStep(1);
                setWords([]);
                setSelectedLetters({});
                setTitleText("");
                resetThemeContent();
              }}
              show={currentStep !== 1}
            />
            {words.length > 0 && (
              <>
                <Box
                  ref={lessonTitleRef}
                  sx={{ ...styles.headlineLessonBox, mt: 2 }}
                >
                  <Typography
                    variant="h6"
                    sx={{ ...styles.headlineLessonBoxTitle, display: "none" }}
                  >
                    <span>3</span> Din lektion
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    flexDirection: {
                      xs: "column",
                      md: "row",
                    },
                  }}
                >
                  <Box
                    sx={{
                      ...styles.freshBox,
                      flex: "1 1 auto",
                      padding: "30px",

                      width: viewMode ? "90%" : "100%",
                      ...(viewMode
                        ? { flex: "0 0 70%" }
                        : { flex: "1 1 auto" }),
                      margin: "0 auto",
                    }}
                  >
                    {isEditingTitle ? (
                      <TextField
                        value={titleText}
                        onChange={(e) => setTitleText(e.target.value)}
                        onBlur={() => {
                          setIsEditingTitle(false);
                          showSnackbar("Titeln har uppdaterats", "success");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setIsEditingTitle(false);
                            showSnackbar("Titeln har uppdaterats", "success");
                          }
                        }}
                        autoFocus
                        fullWidth
                        variant="standard"
                        sx={{
                          backgroundColor: "transparent",
                          "& .MuiInputBase-input": {
                            textAlign: "center",
                            fontWeight: "bold",
                            fontSize: "2.6em",
                            marginBottom: "1.2rem",
                            marginTop: "1.5rem",
                          },
                        }}
                        InputProps={{
                          disableUnderline: true,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          position: "relative",
                          display: "inline-block",
                          margin: "0 auto",
                          width: "100%",
                          mb: 3,
                          mt: 0,
                          ":hover .edit-icon": {
                            opacity: 1,
                          },
                        }}
                      >
                        <Typography
                          variant="h1"
                          sx={{
                            fontWeight: "bold",
                            fontSize: "2.6em",

                            textAlign: "center",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                          }}
                        >
                          {titleText || " "}
                          <Box
                            component="span"
                            sx={{
                              opacity: 0,
                              transition: "opacity 0.2s",
                              display: "inline-flex",
                              gap: 0.5,
                            }}
                            className="edit-icon"
                          >
                            <Tooltip title="Redigera" placement="top">
                              <IconButton
                                size="small"
                                onClick={() => setIsEditingTitle(true)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        borderRadius: "13px",
                      }}
                    >
                      <Grid container spacing={3}>
                        {words.map((word, wordIndex) => (
                          <>
                            <Typography
                              sx={{
                                fontWeight: "700",
                                fontSize: "23px",
                                display: "block",
                                mt: 8,
                                ml: 2,
                                textTransform: "capitalize",
                              }}
                            >
                              {word.word}
                            </Typography>

                            <Grid item xs={12} key={wordIndex}>
                              <Box
                                sx={{
                                  ...styles.freshBox,
                                  p: 2,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  borderRadius: "13px",

                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "#1a2027"
                                      : "#f5f5f5",
                                }}
                              >
                                <Box
                                  sx={{
                                    position: "relative",
                                    width: 250,
                                    height: 250,
                                    flexShrink: 0,
                                  }}
                                >
                                  <HoverableImage
                                    imageUrl={word.image}
                                    isLoading={
                                      isGeneratingImages ||
                                      generatingImageIndex === wordIndex
                                    }
                                    loadingIndex={generatingImageIndex}
                                    currentIndex={wordIndex}
                                    onGenerateClick={() =>
                                      handleImageButtonClick(
                                        "single",
                                        wordIndex,
                                      )
                                    }
                                    userId={user.uid}
                                    onImageUpdate={(newUrl) =>
                                      handleImageUpdate(wordIndex, newUrl)
                                    }
                                    height="250px"
                                    theme={theme}
                                    showSnackbar={showSnackbar}
                                  />
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 2,
                                      mb: 2,
                                    }}
                                  >
                                    {word.letters.map((letter, letterIndex) => (
                                      <Box
                                        key={letterIndex}
                                        onClick={() =>
                                          handleLetterClick(
                                            wordIndex,
                                            letter,
                                            letterIndex,
                                          )
                                        }
                                        sx={{
                                          width: 44,
                                          height: 44,
                                          fontWeight: "600",
                                          borderRadius: "50%",
                                          border: `2px solid ${themeColor}`,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          cursor: "pointer",
                                          backgroundColor: selectedLetters[
                                            wordIndex
                                          ]?.find(
                                            (item) =>
                                              item.index === letterIndex,
                                          )
                                            ? "#8b5cf6"
                                            : "transparent",
                                          color: selectedLetters[
                                            wordIndex
                                          ]?.find(
                                            (item) =>
                                              item.index === letterIndex,
                                          )
                                            ? "#fff"
                                            : `${themeColor}`,
                                          "&:hover": {
                                            backgroundColor: "#8b5cf6",
                                            color: "#fff",
                                          },
                                          transition: "all 0.2s",
                                        }}
                                      >
                                        {letter}
                                      </Box>
                                    ))}
                                  </Box>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center", // Center the entire container vertically
                                      gap: 2,
                                      position: "relative",
                                      height: "100%", // Take full height of parent
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        height: "100%", // Take full height
                                      }}
                                    >
                                      {(selectedLetters[wordIndex] || []).map(
                                        (item, i) => (
                                          <Box
                                            key={i}
                                            sx={{
                                              width: 45,
                                              height: 45,
                                              borderRadius: "50%",
                                              backgroundColor: "#8b5cf6",
                                              color: "#fff",
                                              fontSize: "17px",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            {item.letter}
                                          </Box>
                                        ),
                                      )}
                                    </Box>
                                    {/*
                              <IconButton
                                onClick={() => handleClearWord(wordIndex)}
                                size='small'
                                sx={{ position: 'absolute', bottom: '-20px', right: '0px' }}
                              >
                                <RefreshIcon />
                              </IconButton>
*/}
                                    {isWordComplete(wordIndex) && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          color: "success.main",
                                        }}
                                      >
                                        <CheckCircleIcon />
                                        <Typography>Rätt!</Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </Grid>
                          </>
                        ))}
                      </Grid>
                    </Box>
                  </Box>
                  {!viewMode && (
                    <Box sx={{ position: "relative" }}>
                      <Box
                        sx={{
                          ...styles.lessonControlPanelNuevo2,

                          "& .MuiButton-root": {
                            ...styles.lessonControlPanelNewButtons2,
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "text.secondary" }}
                        >
                          Hantera
                        </Typography>

                        <Button
                          variant={
                            theme.palette.mode === "light"
                              ? "purp"
                              : "contained"
                          }
                          color="primary"
                          onClick={() => handleImageButtonClick("facts")}
                          disabled={isGeneratingImages}
                          startIcon={
                            !isGeneratingImages ? (
                              <ImageIcon />
                            ) : (
                              <CircularProgress size={20} />
                            )
                          }
                        >
                          {isGeneratingImages
                            ? "Genererar bilder..."
                            : "Generera bilder"}
                        </Button>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                            mt: 3,
                          }}
                        >
                          Spara
                        </Typography>

                        <SaveLessonDialog
                          key={titleText}
                          open={saveDialogOpen}
                          onClose={() => setSaveDialogOpen(false)}
                          onSave={handleSaveWithComment}
                          theme={theme}
                          lessonObject={words}
                          lessonName={titleText}
                        />

                        <ImageGenerationModal
                          open={showImageModal}
                          onClose={handleCloseModal}
                          onGenerate={handleGenerateWithInstructions}
                          isGenerating={
                            imageGenerationType === "topic"
                              ? isGeneratingImages
                              : isGeneratingImages
                          }
                          instructions={imageInstructions}
                          setInstructions={setImageInstructions}
                          generationType={imageGenerationType}
                          theme={theme}
                        />

                        <Button
                          variant="print"
                          color="primary"
                          onClick={() =>
                            handlePrintLetterBox(
                              words,
                              selectedLetters,
                              themeColor,
                              titleText,
                            )
                          }
                          startIcon={<PrintIcon />}
                          sx={{ ml: 2 }}
                        >
                          Skriv ut
                        </Button>

                        <Button
                          variant="print"
                          startIcon={<SaveAsIcon />}
                          sx={{ ml: 2 }}
                          onClick={handleSaveLessonClick}
                        >
                          Spara i lektionsbank
                        </Button>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                            mt: 3,
                          }}
                        >
                          Dela
                        </Typography>
                        <ShareLink
                          buttonRef={shareLinkButtonRef}
                          key={lessonId}
                          lessonId={lessonId}
                          lessonData={{
                            words: words,
                            selectedLetters: selectedLetters,
                            titleText: titleText,
                          }}
                          user={user}
                          lessonType={"LetterBoxLesson.js"}
                          onCopy={(message, severity) =>
                            showSnackbar(message, severity)
                          }
                        />

                        <Button
                          variant="success"
                          sx={{}}
                          onClick={() => setShareDialogOpenFriends(true)}
                          startIcon={<ShareIcon />}
                        >
                          Dela med vänner
                        </Button>
                        <Button
                          variant="success"
                          sx={{
                            mt: 2,
                            ml: 2,
                          }}
                          onClick={() => setShareDialogOpen(true)}
                          startIcon={<ShareIcon />}
                        >
                          Dela med elever
                        </Button>
                        <Box>
                          <MoreToCome user={user} />
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}

        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          onShare={handleShare}
          user={user}
          isLessonSharing={true}
          theme={theme}
          lessonName={titleText || ""}
          lessonObject={{
            words: words,
            selectedLetters: selectedLetters,
            titleText: titleText,
          }}
          lessonType={"LetterBoxLesson.js"}
        />

        <ShareDialog
          open={shareDialogOpenFriends}
          onClose={() => setShareDialogOpenFriends(false)}
          onShare={handleShare}
          user={user}
          isLessonSharing={true}
          theme={theme}
          lessonName={titleText || ""}
          lessonObject={{
            words: words,
            selectedLetters: selectedLetters,
            titleText: titleText,
          }}
          lessonType={"LetterBoxLesson.js"}
          shareWithFriends={true}
        />

        <LoadingBackdrop
          isOpen={isSharing}
          imagePath="/img/AI.png"
          loadingText={"Delar lektionen..."}
        />

        <LoadingDialog
          open={isGeneratingLesson}
          onClose={() => {}}
          progressLabel={"Genererar lektion — tar 30-60 sekunder..."}
          hideProgressBar={true}
          title="Genererar lektion. Snart klart..."
          snackbarMessage="Genereringen är snart klar. Vänligen vänta."
          snackbarSeverity="info"
          progressBarHeight="15px"
          progressBarFontSize="0.9rem"
          coffee={false}
        />

        <LoadingDialog
          open={isGeneratingImages}
          onClose={() => {}}
          progressLabel={"Genererar bilderna — tar 30-60 sekunder..."}
          hideProgressBar={true}
          title="Genererar bilderna. Snart klart..."
          snackbarMessage="Genereringen är snart klar. Vänligen vänta."
          snackbarSeverity="info"
          progressBarHeight="15px"
          progressBarFontSize="0.9rem"
          coffee={false}
        />
      </Box>
    </Box>
  );
};

export default LetterBoxLesson;
